<template>
  <v-locale-provider :locale="applicationStore.language">
    <v-layout class="min-h-screen rounded-lg">
      <NavbarDrawer />
      <Navbar />
      <v-main class="min-h-full">
        <div class="container max-w-[1280px] mx-auto px-4 my-12">
          <div>
            <TransitionHeight :watchChange="applicationStore.alertList.length">
              <transition-group
                name="alert-list-transition"
                class="position-relative d-flex flex-column gap"
                tag="div"
              >
                <v-alert
                  :key="`${item.key}${item.date?.getTime() ?? 0}`"
                  v-for="item in applicationStore.alertList"
                  closable
                  :title="i18n.global.t('global.error')"
                  :text="`[${item.date?.toDateString()} ${item.date?.toLocaleTimeString()}] ${i18n.global.t(
                    item.key,
                    { ...item.keyData }
                  )}`"
                  @click:close="applicationStore.removeAlert(item)"
                  :type="item.type === 'Alert' ? 'error' : 'warning'"
                  class="mb-6"
                >
                  <div
                    v-if="item.key === 'app.printLabels.loadingDownload.error'"
                  >
                    <v-btn
                      @click="
                        () => {
                          applicationStore.displayTab = 'history';
                          applicationStore.triggerPdfSearch.emit('search');
                          applicationStore.removeAlert(item);
                        }
                      "
                      variant="tonal"
                      prepend-icon="mdi-open-in-new"
                      class="mt-2"
                      >{{
                        i18n.global.t(
                          "app.printLabels.loadingDownload.errorHistoryLink"
                        )
                      }}</v-btn
                    >
                  </div>
                </v-alert>
              </transition-group>
            </TransitionHeight>

            <div id="inputs-card">
              <v-card :elevation="6">
                <template #title>
                  <div class="flex justify-between items-center">
                    <h1>{{ i18n.global.t("global.userInputs") }}</h1>
                    <div>
                      <v-tooltip
                        :text="i18n.global.t('app.documentation.tooltip')"
                        :open-delay="400"
                        location="top"
                      >
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            variant="plain"
                            color="white"
                            icon="mdi-help-circle"
                            @click="showHelpMenu = true"
                          >
                            <v-icon>mdi-help-circle</v-icon>
                            <v-dialog
                              width="fit-content"
                              max-width="min(960px, 100vw); !important"
                              activator="parent"
                            >
                              <template #default="{ isActive }">
                                <v-card class="max-w-full">
                                  <template #title>
                                    <div
                                      class="flex justify-between items-center w-full"
                                    >
                                      <h2>
                                        {{
                                          i18n.global.t(
                                            "app.documentation.title"
                                          )
                                        }}
                                      </h2>
                                      <v-btn
                                        variant="text"
                                        icon="mdi-close"
                                        @click="
                                          () => {
                                            isActive.value = false;
                                          }
                                        "
                                      ></v-btn>
                                    </div>
                                  </template>
                                  <template #text
                                    ><AppDocumentation />
                                  </template>
                                </v-card>
                              </template>
                            </v-dialog>
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </div>
                  </div>
                </template>
                <template #text>
                  <div>
                    <!-- hide system select when there is no selection to be made (when the system list length is 1) -->
                    <select-system
                      v-if="
                        (systemStore.systems.length !== 1 &&
                          systemStore.loadingState === 'success') ||
                        systemStore.loadingState === 'failed' ||
                        systemStore.loadingState === 'loading'
                      "
                    />
                  </div>
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div id="select-supplier">
                      <select-supplier />
                    </div>
                    <div id="select-matnr">
                      <select-mat-nr />
                    </div>
                  </div>
                </template>
              </v-card>
            </div>

            <div id="view-card" v-if="materialStore.loadingState !== 'initial'">
              <v-card :elevation="6" class="mt-12">
                <v-card-item>
                  <div class="w-full">
                    <v-tabs
                      v-model="applicationStore.displayTab"
                      color="primary"
                      align-tabs="center"
                      fixed-tabs
                      id="tabs"
                    >
                      <v-tab class="transition-none" value="print" key="print">
                        {{ i18n.global.t("app.printLabels.info.print") }}
                      </v-tab>
                      <v-tab
                        class="transition-none"
                        value="cancel"
                        key="cancel"
                      >
                        {{ i18n.global.t("app.cancelSerials.info.nameFull") }}
                      </v-tab>
                      <v-tab
                        class="transition-none"
                        value="history"
                        key="history"
                        @click="() => (showTimeoutHistoryIndicator = false)"
                      >
                        <v-badge
                          :model-value="showTimeoutHistoryIndicator"
                          color="primary"
                          dot
                          :offset-y="-8"
                          :offset-x="-8"
                        >
                          {{ i18n.global.t("app.pdfHistory.info.name") }}
                        </v-badge>
                      </v-tab>
                    </v-tabs>
                  </div>
                  <v-window
                    :touch="false"
                    v-model="applicationStore.displayTab"
                    class="pt-8"
                  >
                    <TransitionHeight>
                      <v-window-item eager key="print" value="print">
                        <label-list />
                      </v-window-item>
                      <v-window-item eager key="cancel" value="cancel">
                        <cancel-serial />
                      </v-window-item>
                      <v-window-item eager key="history" value="history">
                        <download-history />
                      </v-window-item>
                    </TransitionHeight>
                  </v-window>
                </v-card-item>
              </v-card>
            </div>
          </div>
        </div>
      </v-main>

      <v-footer
        name="footer"
        absolute
        app
        color="transparent"
        class="flex justify-between gap-4 !overflow-y-hidden"
      >
        <p class="v-list-item-subtitle break-normal">
          {{ i18n.global.t("app.name") }}
        </p>
        <p class="v-list-item-subtitle break-normal">
          {{ new Date().getFullYear() }} © Rittal GmbH & Co. KG
        </p>
      </v-footer>
    </v-layout>
  </v-locale-provider>
</template>
<script setup lang="ts">
//node modules
import { onMounted, ref, watch } from "vue";
import { useLocale, useDate, useTheme } from "vuetify";
import { usePreferredDark } from "@vueuse/core";

//helpers
import { getCookie, setCookie } from "@/helpers/cookies";
import * as animationHandler from "@/helpers/handleAnimations";

//components
import TransitionHeight from "@/components/SmoothHeight.vue";
import AppDocumentation from "@/components/app/ApplicationDocumentation.vue";
// navbar components
import Navbar from "@/components/app/navbar/NavigationBar.vue";
import NavbarDrawer from "@/components/app/navbar/NavigationDrawer.vue";
// input components
import SelectSystem from "@/components/app/inputs/SelectSystem.vue";
import SelectSupplier from "@/components/app/inputs/SelectSupplier.vue";
import SelectMatNr from "@/components/app/inputs/SelectMatNr.vue";
// view components
import LabelList from "@/components/app/views/label-list/LabelList.vue";
import CancelSerial from "@/components/app/views/CancelSerial.vue";

import DownloadHistory from "@/components/app/views/download-history/DownloadHistory.vue";
//i18n
import { i18n } from "@/locales";
//stores
import {
  useSupplierStore,
  useUserStore,
  useSystemStore,
  useApplicationStore,
  useMaterialStore,
  useLabelStore,
} from "@/store";

const supplierStore = useSupplierStore();
const systemStore = useSystemStore();
const userStore = useUserStore();
const applicationStore = useApplicationStore();
const materialStore = useMaterialStore();
const labelStore = useLabelStore();

// vuetify instances
const vuetifyTheme = useTheme();
const vuetifyLocale = useLocale();
const vuetifyDate = useDate();

// get user
userStore.initUserAuthentication();
// init system store watcher
systemStore.initWatcher();
// get suppliers on selected system change
supplierStore.initWatcher();
// clear material on selecting different supplier
materialStore.initWatcher();

applicationStore.initState();

const showHelpMenu = ref(false);
//
const showTimeoutHistoryIndicator = ref(false);
watch(
  () => labelStore.indicateTimeout,
  (val) => {
    showTimeoutHistoryIndicator.value = val;
  }
);

// some features like the color theme can only be updated from inside a setup function
watch(
  () => applicationStore.theme,
  (theme) => {
    setCookie("color-theme", String(theme), 30);
    vuetifyTheme.global.name.value = theme;
  }
);

// get state form cookies
onMounted(() => {
  const theme = getCookie("color-theme");
  if (theme) {
    applicationStore.setTheme(theme === "light" ? "light" : "dark");
  } else {
    const preferDark = usePreferredDark();

    applicationStore.setTheme(preferDark.value ? "dark" : "light");
  }

  setLocale(applicationStore.language);
});

// update vuetify locale from here (inside a setup function, otherwise vuetify.useLocale() can't find its instance...)
watch(
  () => applicationStore.language,
  (val) => setLocale(val)
);
// Update locale
function setLocale(val: "de" | "en") {
  vuetifyLocale.current.value = val;
  vuetifyDate.locale = val;
}

// handle show label box animation
watch(
  () => materialStore.loadingState,
  async (state, oldState) => {
    if (oldState !== "initial") return;
    await animationHandler.afterSuccessfulMaterialInput();
  }
);
</script>
<style lang="scss" scoped>
.alert-list-transition {
  &-enter-active,
  &-leave-active,
  &-move {
    transition: all 200ms;
  }

  &-leave-active {
    position: absolute;
    width: 100%;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }
}
</style>
